import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { Observable } from 'rxjs';
import { map, retry } from 'rxjs/operators';
import { CompareHelper } from '../../../helpers';
import { AbcCoreDataModule } from './abc-core-data.module';
import { CacheService } from './caching/cache.service';
import { ICoreDataEntity } from './models/dataEntity.model';
import { NetworkTraceObject } from './models/networkTrace.model';
import {
  CheckLicensesModel,
  DeleteObjectsModel,
  EvaluateDefaultValues,
  EvaluateObjectModel,
  EvaluateValue,
  EvaluateValues,
  ExecuteMoveNodeDef,
  ExecuteQueryDocumentDef,
  FilterDocumentsModel,
  NetworkTraceModel,
  ObjectTypeReadInfoModel,
  PersistObjectModel,
  PersistObjectsModel,
  Query,
  QueryColumn,
  QueryColumnSortOrder,
  QueryExpressionModel,
  QueryObjectsModel,
  QueryParameter,
  QueryPathModel,
  QueryValueModel,
  ReadDocumentModel,
  ReadListDataSourceModel,
  ReadObjectTypeInfoModel,
  ReadPicklistItemsModel,
  ReadValidateModel,
  ReportImageInfoQueryDef,
  SaveQueryToDocumentModel,
  SubscribeForNotificationsModel,
  UnsubscribeForNotificationsModel,
} from './models/query.model';
import { ReportImageInfo } from './models/reportImageInfo.model';

@Injectable({
  providedIn: AbcCoreDataModule,
})
export class DataService {
  private readObjectsDataServiceUri: string;
  private manipulateObjectsDataServiceUri: string;
  private readValueDataServiceUri: string;
  private deleteObjectsDataServiceUri: string;
  private persistObjectsDataServiceUri: string;
  private persistObjectDataServiceUri: string;
  private readDocumentDataServiceUri: string;
  private filterDocumentsDataServiceUri: string;
  private objectTypeInfoDataServiceUri: string;
  private readListDataSourceUri: string;
  private functionsInfoDataServiceUri: string;
  private operatorsInfoDataServiceUri: string;
  private validateServiceUri: string;
  private LocalizeExpressionServiceUri: string;
  private UnlocalizeExpressionServiceUri: string;
  private setValueServiceUri: string;
  private getValueServiceUri: string;
  private readPicklistsUriServiceUri: string;
  private readPicklistsItemsUriServiceUri: string;
  private defaultParameterDefinitionsServiceUri: string;
  private objectTypesInfoDataServiceUri: string;
  private readFileUri: string;
  private httpPostRetryCount?: number;
  private networkShortestPathUri: string;
  private networkSubnetUri: string;
  private ExportDocumentResultUri: string;
  private ExecuteQueryDocumentResultUri: string;
  private ExecuteWwMoveNodeResultUri: string;
  private ReadQueryFromDocumentDataServiceUri: string;
  private SaveQueryToDocumentDataServiceUri: string;
  private reversePathToExistsQuery: string;
  private subscribeForNotificationsDataServiceUri: string;
  private unsubscribeForNotificationsDataServiceUri: string;
  private checkSubscriptionForNotificationsDataServiceUri: string;
  private checkLicensesDataServiceUri: string;
  private evaluateValueDataServiceUri: string;
  private evaluateValuesDataServiceUri: string;
  private evaluateDefaultValuesDataServiceUri: string;
  private readTargetObjectsFromPathUri: string;

  constructor(private http: HttpClient, private cacheService: CacheService, private configService: AbcConfigService) {
    this.readObjectsDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.ReadObjects;
    this.manipulateObjectsDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.ManipulateObjects;
    this.deleteObjectsDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.DeleteObjects;
    this.readValueDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.ReadValue;
    this.persistObjectsDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.PersistObjects;
    this.persistObjectDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.PersistObject;
    this.readDocumentDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.ReadDocument;
    this.filterDocumentsDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.FilterDocuments;
    this.objectTypeInfoDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.ObjectTypeInfo;
    this.readListDataSourceUri = this.configService.configuration.WebApi.ServiceURLs.ReadListDataSource;
    this.functionsInfoDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.FunctionsInfo;
    this.operatorsInfoDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.OperatorsInfo;
    this.objectTypesInfoDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.ObjectTypesInfo;
    this.validateServiceUri = this.configService.configuration.WebApi.ServiceURLs.Validate;
    this.LocalizeExpressionServiceUri = this.configService.configuration.WebApi.ServiceURLs.LocalizeExpression;
    this.UnlocalizeExpressionServiceUri = this.configService.configuration.WebApi.ServiceURLs.UnlocalizeExpression;
    this.setValueServiceUri = this.configService.configuration.WebApi.ServiceURLs.SetValue;
    this.getValueServiceUri = this.configService.configuration.WebApi.ServiceURLs.GetValue;
    this.defaultParameterDefinitionsServiceUri =
      this.configService.configuration.WebApi.ServiceURLs.DefaultParameterDefinitions;
    this.readPicklistsUriServiceUri = this.configService.configuration.WebApi.ServiceURLs.ReadPicklists;
    this.readPicklistsItemsUriServiceUri = this.configService.configuration.WebApi.ServiceURLs.ReadPicklistItems;
    this.readFileUri = this.configService.configuration.WebApi.ServiceURLs.ReadFile;
    this.httpPostRetryCount = this.configService.configuration.HttpPostRetryCount;
    this.networkShortestPathUri = this.configService.configuration.WebApi.ServiceURLs.Network_ShortestPath;
    this.networkSubnetUri = this.configService.configuration.WebApi.ServiceURLs.Network_Subnet;
    this.ExportDocumentResultUri = this.configService.configuration.WebApi.ServiceURLs.ExportDocumentResult;
    this.ExecuteQueryDocumentResultUri = this.configService.configuration.WebApi.ServiceURLs.ExecuteQueryDocument;
    this.ExecuteWwMoveNodeResultUri = this.configService.configuration.WebApi.ServiceURLs.ExecuteWwMoveNode;
    this.ReadQueryFromDocumentDataServiceUri =
      this.configService.configuration.WebApi.ServiceURLs.ReadQueryFromDocument;
    this.SaveQueryToDocumentDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.SaveQueryToDocument;
    this.reversePathToExistsQuery = this.configService.configuration.WebApi.ServiceURLs.ReversePathToExistsQuery;
    this.subscribeForNotificationsDataServiceUri =
      this.configService.configuration.WebApi.ServiceURLs.SubscribeForNotifications;
    this.unsubscribeForNotificationsDataServiceUri =
      this.configService.configuration.WebApi.ServiceURLs.UnsubscribeForNotifications;
    this.checkSubscriptionForNotificationsDataServiceUri =
      this.configService.configuration.WebApi.ServiceURLs.CheckSubscriptionForNotifications;
    this.checkLicensesDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.CheckLicenses;
    this.evaluateValueDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.EvaluateValue;
    this.evaluateValuesDataServiceUri = this.configService.configuration.WebApi.ServiceURLs.EvaluateValues;
    this.evaluateDefaultValuesDataServiceUri =
      this.configService.configuration.WebApi.ServiceURLs.EvaluateDefaultValues;
    this.readTargetObjectsFromPathUri = this.configService.configuration.WebApi.ServiceURLs.ReadTargetObjectsFromPath;
  }

  public queryShortestPath(networkTraceDef: NetworkTraceModel): Observable<NetworkTraceObject[]> {
    return this.executeWebApiPOSTMethod(this.networkShortestPathUri, networkTraceDef).pipe(
      map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as object[]) : [])),
      map((data) => data.map((obj) => new NetworkTraceObject(obj)))
    );
  }

  public querySubnet(networkTraceDef: NetworkTraceModel): Observable<NetworkTraceObject[]> {
    return this.executeWebApiPOSTMethod(this.networkSubnetUri, networkTraceDef).pipe(
      map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as object[]) : [])),
      map((data) => data.map((obj) => new NetworkTraceObject(obj)))
    );
  }

  public queryNetworkData(networkTraceDef: NetworkTraceModel): Observable<object[]> {
    return this.executeWebApiPOSTMethod(
      this.networkShortestPathUri.replace('/ShortestPath', '/NetworkData'),
      networkTraceDef
    ).pipe(map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as object[]) : [])));
  }

  public queryReportImageInfo(reportImageInfoDef: ReportImageInfoQueryDef): Observable<ReportImageInfo[]> {
    return this.executeWebApiPOSTMethod(this.ExportDocumentResultUri, reportImageInfoDef).pipe(
      map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as object[]) : [])),
      map((data) => data.map((obj) => new ReportImageInfo(obj)))
    );
  }

  public executeQueryDocument(executeQueryDef: ExecuteQueryDocumentDef): Observable<{ [key: string]: string[] }> {
    return this.executeWebApiPOSTMethod(this.ExecuteQueryDocumentResultUri, executeQueryDef).pipe(
      map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any) : undefined))
    );
  }

  public executeWwMoveNode(executeQueryDef: ExecuteMoveNodeDef): Observable<ICoreDataEntity[]> {
    return this.executeWebApiPOSTMethod(this.ExecuteWwMoveNodeResultUri, executeQueryDef).pipe(
      map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as ICoreDataEntity[]) : []))
    );
  }

  public queryConcreteObjectType(objectType: string, id: string): Observable<string> {
    return this.queryValue(objectType, id, 'TypeName()');
  }

  public queryObjectDisplayText(objectType: string, id: string): Observable<string> {
    return this.queryValue(objectType, id, 'ref:Id');
  }

  public queryValue(objectType: string, id: string, opath: string): Observable<string> {
    const query = new QueryValueModel();
    query.OPath = 'Type(' + objectType + ')[Id=$Id].GetValue(' + opath + ')';

    query.Parameters = [new QueryParameter('Id', id)];

    return this.executeReadValueQuery(query).pipe(map((data) => data as any as string));
  }

  public queryNumber(
    objectType: string,
    id: string,
    opath: string,
    ...parameters: QueryParameter[]
  ): Observable<number> {
    const query = new QueryValueModel();
    query.OPath = 'Type(' + objectType + ')[Id=$Id].GetValue(' + opath + ')';

    query.Parameters = [new QueryParameter('Id', id)];
    if (parameters) {
      for (const p of parameters) {
        query.Parameters.push(p);
      }
    }

    return this.executeReadValueQuery(query).pipe(map((data) => data as any as number));
  }

  public createSimpleQuery(
    objectSpace: string,
    objectType: string,
    oPath: string,
    columns?: QueryColumn[],
    parameters?: QueryParameter[]
  ): QueryObjectsModel {
    const queryDocument: QueryObjectsModel = new QueryObjectsModel();
    queryDocument.ObjectSpaceName = objectSpace;

    const mainQuery: Query = new Query();
    mainQuery.ObjectType = objectType;
    mainQuery.OPath = oPath;
    mainQuery.Columns = columns;
    queryDocument.ObjectQueries = [mainQuery];
    queryDocument.Parameters = parameters;

    return queryDocument;
  }

  public createQueryColumn(
    oPath: string,
    name?: string,
    sortOrder?: QueryColumnSortOrder,
    isFilter?: boolean,
    isHidden?: boolean,
    isInvariant?: boolean,
    format?: string
  ): QueryColumn {
    const column: QueryColumn = new QueryColumn();
    column.OPath = oPath;
    column.Name = name ? name : oPath;
    sortOrder && (column.SortOrder = sortOrder);
    isFilter && (column.IsFilter = isFilter);
    isHidden && (column.IsHidden = isHidden);
    isInvariant && (column.IsInvariant = isInvariant);
    format && (column.Format = format);
    return column;
  }

  public executeReadObjectsQuery(
    query: QueryObjectsModel,
    noCache?: boolean,
    refreshCache?: boolean
  ): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.readObjectsDataServiceUri, query, noCache, refreshCache);
  }

  public executeManipulateObjectsQuery(query: QueryObjectsModel): Observable<{ [key: string]: string[] }> {
    return this.executeWebApiPOSTMethod(this.manipulateObjectsDataServiceUri, query, true).pipe(
      map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any as { [key: string]: string[] }) : undefined))
    );
  }

  public executeReadObjectsQueryXML(query: QueryObjectsModel): Observable<string> {
    return this.executeWebApiPOSTMethodXml(this.readObjectsDataServiceUri, query);
  }

  public executeDeleteObjectQuery(query: DeleteObjectsModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.deleteObjectsDataServiceUri, query);
  }

  public executeReadValueQuery(query: QueryValueModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.readValueDataServiceUri, query);
  }

  public executePersistObjectsQuery(query: PersistObjectsModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.persistObjectsDataServiceUri, query, true);
  }

  public executePersistObjectQuery(query: PersistObjectModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.persistObjectDataServiceUri, query, true);
  }

  public executeReadDocumentQuery(query: ReadDocumentModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.readDocumentDataServiceUri, query);
  }

  public executeFilterDocumentsQuery(query: FilterDocumentsModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.filterDocumentsDataServiceUri, query);
  }

  public executeReadListDataSource(
    query: ReadListDataSourceModel,
    noCache?: boolean,
    refreshCache?: boolean
  ): Observable<any> {
    return this.executeWebApiPOSTMethod(this.readListDataSourceUri, query, noCache, refreshCache);
  }

  public executeObjectTypeInfoQuery(query: ReadObjectTypeInfoModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.objectTypeInfoDataServiceUri, query);
  }

  public executeFunctionsInfoQuery(query: ReadObjectTypeInfoModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.functionsInfoDataServiceUri, query);
  }

  public executeOperatorsInfoQuery(query: ReadObjectTypeInfoModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.operatorsInfoDataServiceUri, query);
  }

  public executeObjectTypesInfoQuery(query: ReadListDataSourceModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.objectTypesInfoDataServiceUri, query);
  }

  public executeValidateQuery(query: ReadValidateModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.validateServiceUri, query);
  }
  public executeLocalizeExpressionQuery(query: QueryExpressionModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.LocalizeExpressionServiceUri, query);
  }
  public executeUnlocalizeExpressionQuery(query: QueryExpressionModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.UnlocalizeExpressionServiceUri, query);
  }

  public executeSetValueQuery(query: QueryParameter): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.setValueServiceUri, query);
  }

  public executeGetValueQuery(query: QueryParameter): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.getValueServiceUri, query);
  }

  public executeReadPicklistsQuery(query: ReadListDataSourceModel): Observable<any> {
    return this.executeWebApiPOSTMethod(this.readPicklistsUriServiceUri, query);
  }

  public executeReadPicklistsItemsQuery(query: ReadPicklistItemsModel): Observable<any> {
    return this.executeWebApiPOSTMethod(this.readPicklistsItemsUriServiceUri, query);
  }

  public executeDefaultParameterDefinitionsQuery(query: ReadListDataSourceModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.defaultParameterDefinitionsServiceUri, query);
  }

  public executeReadFileQueryBlob(query, noCache?: boolean, refreshCache?: boolean): Observable<Blob> {
    return this.httpPostBlob(this.readFileUri, query, { responseType: 'blob' }, noCache, refreshCache);
  }

  public executeReadFileQuery(query, noCache?: boolean, refreshCache?: boolean): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.readFileUri, query, noCache, refreshCache);
  }

  public exportDocumentResult(query, noCache?: boolean, refreshCache?: boolean): Observable<Blob> {
    return this.httpPostBlob(this.ExportDocumentResultUri, query, { responseType: 'blob' }, noCache, refreshCache);
  }

  public executeReadObjectTypeInfo(query: ObjectTypeReadInfoModel): Observable<any> {
    return this.executeWebApiPOSTMethod(this.objectTypeInfoDataServiceUri, query);
  }

  public executeReadQueryFromDocumentQuery(query: ReadDocumentModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.ReadQueryFromDocumentDataServiceUri, query);
  }

  public executeSaveQueryToDocumentQuery(query: SaveQueryToDocumentModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.SaveQueryToDocumentDataServiceUri, query);
  }

  public executeReversePathToExistsQuery(query: QueryPathModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.reversePathToExistsQuery, query);
  }

  public subscribeForNotifications(query: SubscribeForNotificationsModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.subscribeForNotificationsDataServiceUri, query);
  }

  public unsubscribeForNotifications(query: UnsubscribeForNotificationsModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.unsubscribeForNotificationsDataServiceUri, query);
  }

  public checkSubscriptionForNotifications(query: UnsubscribeForNotificationsModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.checkSubscriptionForNotificationsDataServiceUri, query);
  }

  public checkLicenses(query: CheckLicensesModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.checkLicensesDataServiceUri, query);
  }

  public evaluateValue(query: EvaluateValue): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.evaluateValueDataServiceUri, query);
  }

  public evaluateValues(query: EvaluateValues): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.evaluateValuesDataServiceUri, query);
  }

  public evaluateDefaultValues(query: EvaluateDefaultValues): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.evaluateDefaultValuesDataServiceUri, query);
  }

  public readTargetObjectsFromPath(query: EvaluateObjectModel): Observable<ArrayBuffer> {
    return this.executeWebApiPOSTMethod(this.readTargetObjectsFromPathUri, query);
  }

  public executeWebApiPOSTMethod(
    url: string,
    body: any,
    noCache?: boolean,
    refreshCache?: boolean
  ): Observable<ArrayBuffer> {
    const options: any = {
      observe: 'body',
    };
    return this.httpPost(url, body, options, noCache, refreshCache);
  }

  public executeWebApiPOSTMethodBlob(
    url: string,
    body: any,
    noCache?: boolean,
    refreshCache?: boolean
  ): Observable<Blob> {
    return this.httpPostBlob(
      url,
      body,
      {
        observe: 'body',
        responseType: 'blob',
      },
      noCache,
      refreshCache
    );
  }
  public executeWebApiPOSTMethodXml(url: string, body: any): Observable<string> {
    return this.httpPostText(url, body, { responseType: 'text' });
  }

  public getJsonFile(url: string): Observable<Object> {
    return this.httpGetObject(url);
  }

  private httpPost(
    url: string,
    body: any,
    options: any,
    noCache?: boolean,
    refreshCache?: boolean
  ): Observable<ArrayBuffer> {
    return this.cacheService
      .httpPost(this.http, url, body, options, noCache, refreshCache)
      .pipe(retry(this.httpPostRetryCount));
  }

  private httpPostBlob(
    url: string,
    body: any,
    options: any,
    noCache?: boolean,
    refreshCache?: boolean
  ): Observable<Blob> {
    return this.cacheService
      .httpPostBlob(this.http, url, body, options, noCache, refreshCache)
      .pipe(retry(this.httpPostRetryCount));
  }

  private httpPostText(url: string, body: any, options: any): Observable<string> {
    return this.cacheService.httpPostText(this.http, url, body, options).pipe(retry(this.httpPostRetryCount));
  }

  private httpGetObject(url: string): Observable<Object> {
    return this.cacheService.httpGetObject(this.http, url).pipe(retry(this.httpPostRetryCount));
  }
}
